import React, { useEffect, useMemo, useState } from "react";
import { Table, Input, Button, Space, Popconfirm, Tooltip, Spin, Tag } from "antd";
import { AiOutlineEdit, AiOutlineDelete, AiFillCheckCircle, AiFillMinusCircle } from "react-icons/ai";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "../../../components/utils/Interceptors";
import updateData from "../../../hooks/updateData";

const Customers = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [filterText, setFilterText] = useState("");
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));

  const filteredItems = useMemo(() => {
    return customers.filter(
      (item) =>
        item.cust_fullname.toLowerCase().includes(filterText.toLowerCase()) ||
        item.cust_email.toLowerCase().includes(filterText.toLowerCase())
    );
  }, [customers, filterText]);

  useEffect(() => {
    const getAllCustomers = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_APIURL}/vendor/getallcustomer`,
          {
            headers: {
              Authorization: `Bearer ${user.token}`,
            },
          }
        );
        if (response.data.status_code === 200 && response.data.data) {
          setCustomers(response.data.data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    getAllCustomers();
  }, []);

  const changeVendorStatus = async (id, status) => {
    setLoading(true);
    const statusValue = status ? 0 : 1;
    const res = await updateData("vendor/updatecustomer", { id, status: statusValue });

    if (res.status_code === 200) {
      setCustomers((prev) =>
        prev.map((c) => (c.cust_id === id ? { ...c, status: statusValue } : c))
      );
    }
    setLoading(false);
  };

  const deleteItem = async (id) => {
    try {
      setLoading(true);
      const res = await axios.delete(
        `${process.env.REACT_APP_APIURL}/vendor/deletecustomer/${id}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      if (res.data.status_code === 200) {
        setCustomers((prev) => prev.filter((c) => c.cust_id !== id));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: t("Full_Name"),
      dataIndex: "cust_fullname",
      key: "fullname",
      sorter: (a, b) => a.cust_fullname.localeCompare(b.cust_fullname),
    },
    {
      title: t("form_email"),
      dataIndex: "cust_email",
      key: "email",
    },
    {
      title: t("Cus_phone"),
      dataIndex: "cust_phone",
      key: "phone",
    },
    {
      title: t("Action"),
      key: "action",
      render: (_, row) => (
        <Space>
          <Tooltip title={t("Edit")}>
            <Link
              to={`/vendor/customer/edit/${row.cust_id}`}
              className="text-secondary hover:text-primary"
            >
              <AiOutlineEdit size={18} />
            </Link>
          </Tooltip>

          <Popconfirm
            title={t("delete_alert_title")}
            description={t("delete_alert_des")}
            onConfirm={() => deleteItem(row.cust_id)}
            okText={t("yes")}
            cancelText={t("no")}
            okButtonProps={{
              style: { backgroundColor: '#F4900C', borderColor: '#F4900C' } // Red button
            }}
          >
            <Tooltip title={t("Delete")}>
              <span className="text-error cursor-pointer">
                <AiOutlineDelete size={18} />
              </span>
            </Tooltip>
          </Popconfirm>

          <Tooltip title={row.status === 0 ? t("unsuspend") : t("Suspend")}>
            <span
              onClick={() => changeVendorStatus(row.cust_id, row.status)}
              className={`cursor-pointer ${row.status === 0 ? "text-green-700" : "text-red-700"
                }`}
            >
              {row.status === 0 ? (
                <AiFillCheckCircle size={18} />
              ) : (
                <AiFillMinusCircle size={18} />
              )}
            </span>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">{t("customers")}</h2>
        <Link to="/vendor/add-customer">
          <button className="btn btn-primary btn-sm w-full rounded-md text-white"  >{t("add_customers")}</button>
        </Link>
      </div>

      <Input.Search
        placeholder={t("Search")}
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
        allowClear
        style={{ marginBottom: 16, maxWidth: 300 }}
      />

      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={filteredItems}
          rowKey="cust_id"
          pagination={{ pageSize: 10 }}
          locale={{
            emptyText: <span className="text-gray-400">{t("No records")}</span>,
          }}
        />
      </Spin>
    </>
  );
};

export default Customers;
